import { Box, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { isValidElement, useMemo } from 'react';
import { AppError } from '@/utils/api';
import { AppErrorRendererCard } from './AppErrorRenderer';

const DEFAULT_FIELD_NAME_MAP = {
  code: 'Código',
};

const SnackbarMessage = ({ title, subtitle }) => {
  if (!subtitle) return title;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.2 }}>{title}</Typography>
      <Typography
        sx={{
          mt: 0.7,
          fontSize: 13,
          color: 'text.secondary',
          lineHeight: 1.35,
          whiteSpace: 'pre-line',
          overflowWrap: 'anywhere',
        }}
      >
        {subtitle}
      </Typography>
    </Box>
  );
};

SnackbarMessage.propTypes = {
  title: PropTypes.node,
  subtitle: PropTypes.node,
};

const buildMessage = (message, subtitle) => {
  if (!subtitle) return message;

  return <SnackbarMessage title={message} subtitle={subtitle} />;
};

export const useNotify = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  return useMemo(() => {
    const notify = (message, variant, options = {}) => {
      const notistackOptions = { ...options };
      const { subtitle } = notistackOptions;
      delete notistackOptions.subtitle;
      delete notistackOptions.variant;
      return enqueueSnackbar(buildMessage(message, subtitle), { ...notistackOptions, variant });
    };

    const success = (message, options) => notify(message, 'success', options);
    const warning = (message, options) => notify(message, 'warning', options);
    const info = (message, options) => notify(message, 'info', options);

    const error = (input, options = {}) => {
      if (typeof input === 'string' || isValidElement(input)) return notify(input, 'error', options);

      const normalizedError = input?.isAppError ? input : new AppError(input);
      const fieldErrors = Array.isArray(normalizedError.fieldErrors) ? normalizedError.fieldErrors : [];
      const { fieldNameMap = DEFAULT_FIELD_NAME_MAP, dataTestId = 'api-error-renderer', ...notistackOptions } = options;

      if (fieldErrors.length === 0) {
        return notify(
          <AppErrorRendererCard dataTestId={dataTestId} message={normalizedError.message} />,
          'error',
          notistackOptions
        );
      }

      return fieldErrors.map(({ field, errors }) =>
        (Array.isArray(errors) ? errors : []).map((errorMessage, index) =>
          notify(
            <AppErrorRendererCard
              key={`${field}-${index}`}
              field={field}
              message={normalizedError.message}
              errorMsg={errorMessage}
              fieldNameMap={fieldNameMap}
              hasFieldErrors={fieldErrors.length > 0}
              dataTestId={dataTestId}
            />,
            'error',
            notistackOptions
          )
        )
      );
    };

    return { success, error, warning, info, close: closeSnackbar };
  }, [closeSnackbar, enqueueSnackbar]);
};
